/**
 * Club API client.
 * All endpoints live under /api/clubs.
 */
import api from './client';

// ── Club CRUD ─────────────────────────────────────────────────────────────

/**
 * Create a new club.
 * @param {{ name: string, description?: string, color?: string }} data
 */
export const createClub = async (data) => {
  const response = await api.post('/clubs/', data);
  return response.data;
};

/**
 * List all clubs the authenticated user is an approved member of.
 * @returns {Promise<Array>}
 */
export const getMyClubs = async () => {
  const response = await api.get('/clubs/');
  return response.data;
};

/**
 * Get full club details (requires membership).
 * @param {number} clubId
 */
export const getClub = async (clubId) => {
  const response = await api.get(`/clubs/${clubId}`);
  return response.data;
};

/**
 * Update club name/description/color.
 * @param {number} clubId
 * @param {{ name?: string, description?: string, color?: string }} data
 */
export const updateClub = async (clubId, data) => {
  const response = await api.put(`/clubs/${clubId}`, data);
  return response.data;
};

/**
 * Delete a club (admin only).
 * @param {number} clubId
 */
export const deleteClub = async (clubId) => {
  const response = await api.delete(`/clubs/${clubId}`);
  return response.data;
};

/**
 * Regenerate the club invite link (admin only). Invalidates the old token.
 * @param {number} clubId
 */
export const regenerateInviteLink = async (clubId) => {
  const response = await api.post(`/clubs/${clubId}/regenerate-invite`);
  return response.data;
};

// ── Join flow ─────────────────────────────────────────────────────────────

/**
 * Get club preview for the join landing page (public — no auth required).
 * @param {string} inviteToken
 */
export const getClubPreview = async (inviteToken) => {
  const response = await api.get(`/clubs/invite/${inviteToken}`);
  return response.data;
};

/**
 * Submit a join request via invite link.
 * @param {string} inviteToken
 */
export const requestToJoin = async (inviteToken) => {
  const response = await api.post(`/clubs/invite/${inviteToken}/request`);
  return response.data;
};

// ── Membership ────────────────────────────────────────────────────────────

/**
 * List approved members of a club.
 * @param {number} clubId
 */
export const getClubMembers = async (clubId) => {
  const response = await api.get(`/clubs/${clubId}/members`);
  return response.data;
};

/**
 * List pending join requests (admin only).
 * @param {number} clubId
 */
export const getPendingRequests = async (clubId) => {
  const response = await api.get(`/clubs/${clubId}/requests`);
  return response.data;
};

/**
 * Approve a pending join request (admin only).
 * @param {number} clubId
 * @param {number} userId
 */
export const approveRequest = async (clubId, userId) => {
  const response = await api.post(`/clubs/${clubId}/requests/${userId}/approve`);
  return response.data;
};

/**
 * Reject a pending join request (admin only).
 * @param {number} clubId
 * @param {number} userId
 */
export const rejectRequest = async (clubId, userId) => {
  const response = await api.post(`/clubs/${clubId}/requests/${userId}/reject`);
  return response.data;
};

/**
 * Remove a member from a club (admin) or leave a club (self).
 * @param {number} clubId
 * @param {number} userId
 */
export const removeMember = async (clubId, userId) => {
  const response = await api.delete(`/clubs/${clubId}/members/${userId}`);
  return response.data;
};

/**
 * Change a member's role (admin only).
 * @param {number} clubId
 * @param {number} userId
 * @param {'admin'|'member'} role
 */
export const updateMemberRole = async (clubId, userId, role) => {
  const response = await api.put(`/clubs/${clubId}/members/${userId}/role`, { role });
  return response.data;
};

// ── Files ─────────────────────────────────────────────────────────────────

/**
 * List club files with optional filters.
 * @param {number} clubId
 * @param {{ memberId?: number, raceId?: number }} filters
 */
export const getClubFiles = async (clubId, filters = {}) => {
  const params = {};
  if (filters.memberId) params.member_id = filters.memberId;
  if (filters.raceId) params.race_id = filters.raceId;
  const response = await api.get(`/clubs/${clubId}/files`, { params });
  return response.data;
};

/**
 * Contribute a running file from personal library to a club.
 * @param {number} clubId
 * @param {number} fileId
 */
export const contributeFile = async (clubId, fileId) => {
  const response = await api.post(`/clubs/${clubId}/files`, { file_id: fileId, sport_type: 'running' });
  return response.data;
};

/**
 * Contribute a bike file from personal library to a club.
 * @param {number} clubId
 * @param {number} fileId
 */
export const contributeBikeFile = async (clubId, fileId) => {
  const response = await api.post(`/clubs/${clubId}/files`, { file_id: fileId, sport_type: 'biking' });
  return response.data;
};

/**
 * Remove a file from a club library.
 * @param {number} clubId
 * @param {number} clubFileId
 */
export const removeClubFile = async (clubId, clubFileId) => {
  const response = await api.delete(`/clubs/${clubId}/files/${clubFileId}`);
  return response.data;
};

export const downloadClubFile = async (clubId, clubFileId, filename) => {
  const response = await api.get(`/clubs/${clubId}/files/${clubFileId}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `club_file_${clubFileId}.fit`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Fetch a club FIT file as a Blob without triggering a browser download.
 * Used when the file needs to be passed to another API (e.g. prediction as course).
 */
export const getClubFileBlob = async (clubId, clubFileId) => {
  const response = await api.get(`/clubs/${clubId}/files/${clubFileId}/download`, { responseType: 'blob' });
  return response.data;
};

/**
 * Analyze a club file — returns full workout_data (same shape as upload).
 * Uses cached RunningAnalysisSession when available.
 * @param {number} clubId
 * @param {number} clubFileId
 */
export const analyzeClubFile = async (clubId, clubFileId) => {
  const response = await api.post(`/clubs/${clubId}/files/${clubFileId}/analyze`);
  return response.data;
};

// ── Leaderboard ───────────────────────────────────────────────────────────

/**
 * Get ranked club files for the leaderboard.
 * @param {number} clubId
 * @param {{ raceId?: number }} filters
 */
export const getClubLeaderboard = async (clubId, filters = {}) => {
  const params = {};
  if (filters.raceId) params.race_id = filters.raceId;
  const response = await api.get(`/clubs/${clubId}/leaderboard`, { params });
  return response.data;
};

// ── Predictions ───────────────────────────────────────────────────────────

/**
 * List predictions shared to a club.
 * @param {number} clubId
 * @param {{ raceId?: number }} filters
 */
export const getClubPredictions = async (clubId, filters = {}) => {
  const params = {};
  if (filters.raceId) params.race_id = filters.raceId;
  const response = await api.get(`/clubs/${clubId}/predictions`, { params });
  return response.data;
};

/**
 * Share a prediction to a club.
 * @param {number} clubId
 * @param {string} predictionId
 */
export const shareClubPrediction = async (clubId, predictionId) => {
  const response = await api.post(`/clubs/${clubId}/predictions`, { prediction_id: predictionId });
  return response.data;
};

/**
 * List all predictions from all clubs the current user is a member of.
 */
export const getMyClubRunningPredictions = async () => {
  const response = await api.get('/clubs/my/predictions');
  return response.data;
};

/**
 * Remove a shared prediction from a club.
 * @param {number} clubId
 * @param {number} clubPredictionId
 */
export const removeClubPrediction = async (clubId, clubPredictionId) => {
  const response = await api.delete(`/clubs/${clubId}/predictions/${clubPredictionId}`);
  return response.data;
};

// ── Activity feed ─────────────────────────────────────────────────────────

/**
 * Get recent activity events for a club (last 50).
 * @param {number} clubId
 */
export const getClubEvents = async (clubId) => {
  const response = await api.get(`/clubs/${clubId}/events`);
  return response.data;
};

// ── Notification badge ────────────────────────────────────────────────────

/**
 * Get total pending join requests across all clubs the user admins.
 * Used for the notification badge on the "My Clubs" button.
 */
/**
 * Return all files from all clubs the current user is an approved member of.
 * Each record includes club_id and club_name.
 */
export const getMyClubFiles = async () => {
  const response = await api.get('/clubs/my/files');
  return response.data;
};

export const getMyClubBikeFiles = async () => {
  const response = await api.get('/clubs/my/bike-files');
  return response.data;
};

export const analyzeClubBikeFile = async (clubId, clubFileId) => {
  const response = await api.post(`/clubs/${clubId}/files/${clubFileId}/analyze`);
  return response.data;
};

export const getMyPendingCount = async () => {
  const response = await api.get('/clubs/my/pending-count');
  return response.data;
};
